import {
  handleCoordinatorCreated,
  handleCoordinatorPhaseUpdated,
  mapToolStatus,
  mergeAttachmentContentsByTypeAndContent,
  mergeToolCallsById,
  normalizeClawMediaType,
  pickString,
  type SessionEventBase,
} from '../../_utils';
import type { ChatMessageItem, ChatMessageToolCall, ChatRole } from '../../types';
import { ACTIONABLE_TURN_EVENT_TYPES, appendUniqueMessage, RESUMABLE_TYPES } from './helpers';
import type { EventHandlerCtx } from './types';

const appendMessageId = (source: string[] | undefined, messageId: string | undefined): string[] | undefined => {
  if (!messageId) return source;
  return Array.from(new Set([...(source ?? []), messageId]));
};

const appendStreamSeq = (source: string[] | undefined, streamSeq: number | undefined): string[] | undefined => {
  if (streamSeq === undefined) return source;
  return Array.from(new Set([...(source ?? []), String(streamSeq)]));
};

/**
 * message.created 的合并规则（存活期与"收尾后迟到正文"两条路径共用，避免行为分叉）：
 * - content 保留首条非空，后续不覆盖（防止后端截断/审核版本盖掉更完整的累积结果）；
 * - streamSeqs 去重、shareMessageIds/serverMessageId/createdAt 按既有规则合并。
 */
const mergeMessageCreated = (
  message: ChatMessageItem,
  args: { content: string; serverMessageId?: string; streamSeq?: number; createdAt: string },
): ChatMessageItem => ({
  ...message,
  content: message.content?.trim() ? message.content : args.content,
  serverMessageId: message.serverMessageId ?? args.serverMessageId,
  streamSeqs: appendStreamSeq(message.streamSeqs, args.streamSeq),
  shareMessageIds: appendMessageId(message.shareMessageIds, args.serverMessageId),
  createdAt: message.createdAt ?? args.createdAt,
  thinkingLoading: false,
});

/** default 路径：完整 turn 生命周期事件聚合到当前 assistant 消息。 */
export function handleDefaultEvent(event: SessionEventBase, ctx: EventHandlerCtx) {
  const {
    setMessages,
    setPhases,
    isStreamingRef,
    activeTurnIdRef,
    settledToolCallIdsRef,
    completedTurnIdsRef,
    updateAssistantMessage,
    setAssistantErrorMessage,
    markStreamCompleted,
    resumeStreamingFromRemoteEvent,
    failedFallbackMessage,
  } = ctx;

  const type = event.type;
  const payload = event.payload ?? {};

  const toolCallId = pickString(payload.tool_call_id);
  const taskId = pickString(payload.task_id);
  const source = pickString(payload.source);
  const taskType = pickString(payload.task_type);

  // tool.started/completed 的异步触发：tool_call_id === task_id 是铁证
  // （正常 turn 内 tool_call_id 是 `call_-xxx`，不会等于 task_id）。
  // 内容由对应的 message.created 承载，此处直接忽略。
  if (
    (type === 'conversation.tool.started' || type === 'conversation.tool.completed') &&
    taskId &&
    toolCallId === taskId
  ) {
    return;
  }

  // 已结束 turn 的迟到 tool 事件：后端偶发会在 turn.completed 之后再派发同一 tool_call_id
  // 的 started/completed（且 turn_id 可能换成全新值）。不拦下来会触发下方 resume
  // 把已完成消息重新置回 thinkingLoading，且不会再有 turn.completed 来收尾。
  if (
    (type === 'conversation.tool.started' || type === 'conversation.tool.completed') &&
    toolCallId &&
    settledToolCallIdsRef.current.has(toolCallId)
  ) {
    return;
  }

  // 异步任务（cron/spawn）：后端会把它们挂到当前 turn 上，不能依赖 !event.turn_id 区分；
  // 必须靠 source/task_type 识别，否则会把内容错误覆盖到 assistant 消息上。
  // 识别出来后作为独立的 cron/spawn 消息插进对话流，和 user/assistant 一起按顺序渲染。
  if (type === 'conversation.message.created') {
    const asyncRole: ChatRole | null =
      source === 'cron' || taskType === 'cron' ? 'cron' : source === 'spawn' || taskType === 'spawn' ? 'spawn' : null;

    if (asyncRole) {
      const content = pickString(payload.content);
      if (!content) return;

      const messageId = pickString(payload.message_id);
      const itemId = messageId
        ? `${asyncRole}-${messageId}`
        : `${asyncRole}-${taskId || 'unknown'}-${event.stream_seq ?? Date.now()}`;
      const createdAt = new Date(event.timestamp ?? Date.now()).toISOString();

      appendUniqueMessage(setMessages, {
        id: itemId,
        role: asyncRole,
        content,
        createdAt,
      });
      return;
    }
  }

  if (type === 'coordinator.created') {
    setPhases((prev) => handleCoordinatorCreated(payload, prev) ?? prev);
    return;
  }

  // 必须在下方 turnId / isStreamingRef 守卫之前处理
  if (type === 'coordinator.phase.updated') {
    setPhases((prev) => handleCoordinatorPhaseUpdated(payload, prev) ?? prev);
    return;
  }

  // 不在此处 markStreamCompleted：
  // - paused 状态下该事件常与 message.created + turn.completed 一同到达，
  //   提前收尾会清空 assistantMessageIdRef，导致后续 message.created 找不到目标消息。
  // - phases 字段在 coordinator.updated 中往往不下发；空 phases 复用
  //   handleCoordinatorCreated 会把已有 phases 清空，UI 闪烁。
  if (type === 'coordinator.updated') {
    if (Array.isArray(payload.phases)) {
      setPhases((prev) => handleCoordinatorCreated(payload, prev) ?? prev);
    }
    return;
  }

  // switch 不消费的事件类型（assistant.state.changed / turn.accepted / turn.started 等）在此拦下：
  // 后端偶发给这类事件带上"上一轮"的 turn_id 并抢在本轮首个真实事件之前到达，若放行会走到下方
  // turn 绑定处把 activeTurnIdRef 锁死到旧 turn，使本轮真正的 thinking/tool/message/turn.completed
  // 全部命中 turn 不匹配守卫被丢弃 —— 表现为"思考中"永不收尾。cron/spawn/coordinator 事件已在上方
  // 分流处理，不受影响。
  if (!ACTIONABLE_TURN_EVENT_TYPES.has(type)) return;

  const turnId = event.turn_id;
  if (!turnId) {
    // 没有 turn_id 且不带 cron/spawn 标记的 message.created（例如通过 message 工具直接发出的内容）：
    // 作为独立 assistant 消息插入对话流，避免内容被静默丢弃。
    if (type === 'conversation.message.created') {
      const content = pickString(payload.content);
      if (!content) return;
      const messageId = pickString(payload.message_id);
      const itemId = messageId ? `assistant-${messageId}` : `assistant-noturn-${event.stream_seq ?? Date.now()}`;
      const createdAt = new Date(event.timestamp ?? Date.now()).toISOString();
      appendUniqueMessage(setMessages, {
        id: itemId,
        role: 'assistant',
        content,
        serverMessageId: messageId || undefined,
        streamSeqs: appendStreamSeq(undefined, event.stream_seq),
        createdAt,
        thinkingLoading: false,
      });
    }
    // spawn agent 发出的 artifact（无 turn_id）：插入独立 assistant 消息，让文件出现在对话流里。
    if (type === 'conversation.artifact.created') {
      const mediaPath = pickString(payload.object_key) || pickString(payload.media_path);
      if (!mediaPath) return;
      const messageId = pickString(payload.message_id);
      const normalizedType = normalizeClawMediaType(pickString(payload.media_type));
      const itemId = messageId
        ? `assistant-artifact-${messageId}`
        : `assistant-artifact-noturn-${event.stream_seq ?? Date.now()}`;
      const createdAt = new Date(event.timestamp ?? Date.now()).toISOString();
      appendUniqueMessage(setMessages, {
        id: itemId,
        role: 'assistant',
        content: '',
        serverMessageId: messageId || undefined,
        streamSeqs: appendStreamSeq(undefined, event.stream_seq),
        createdAt,
        thinkingLoading: false,
        fileList: [{ type: normalizedType, content: mediaPath }],
      });
    }
    return;
  }

  // 已收尾 turn 的迟到 conversation.* 事件：后端偶发复用一个已收到 turn.completed/failed
  // 的 turn_id 来跑后台 phase-worker（spawn 子 agent），其 tool/thinking/message 事件不带
  // source/coordinator_id 等判别字段。若放行会经下方 resume 复活已收尾的主气泡、并把子 agent
  // 的内部工具并进它的 tool_calls。仅当它不是当前正在流式的 turn 时丢弃（正在流式的 turn
  // 由 activeTurnIdRef 守卫，不会进 completedTurnIds）。
  if (turnId !== activeTurnIdRef.current && completedTurnIdsRef.current?.has(turnId)) {
    // 例外：后端偶发把本轮正文的 message.created 排在 turn.completed 之后送达（事件乱序），
    // 此时 turn 已收尾。按 turn_id 找回本轮那条主气泡（绑定时已盖 turnId），套用与存活期
    // 完全一致的 mergeMessageCreated 写入正文——不新造判据、不覆盖已有正文、按 seq 去重。
    // 找不到对应主气泡（真·后台 phase-worker，没有主气泡）则维持丢弃。
    if (type === 'conversation.message.created') {
      const content = pickString(payload.content);
      if (!content) return;
      const serverMessageId = pickString(payload.message_id) || undefined;
      const createdAt = new Date(event.timestamp ?? Date.now()).toISOString();
      setMessages((prev) => {
        const idx = prev.findIndex((m) => m.role === 'assistant' && m.turnId === turnId);
        if (idx < 0) return prev;
        const next = [...prev];
        next[idx] = mergeMessageCreated(prev[idx], {
          content,
          serverMessageId,
          streamSeq: event.stream_seq,
          createdAt,
        });
        return next;
      });
    }
    return;
  }

  // 本端没起 turn 时（刷新后远端续传），仅当事件代表 turn 仍在产出才恢复；
  // turn.completed/failed 不主动恢复，避免一条尾事件凭空建占位再立即收尾。
  if (!isStreamingRef.current) {
    if (!RESUMABLE_TYPES.has(type)) return;
    resumeStreamingFromRemoteEvent({ turnId });
  }

  if (!activeTurnIdRef.current) {
    activeTurnIdRef.current = turnId;
    // 首次绑定时给本轮主气泡盖上 turnId：收尾后若正文乱序迟到（turn.completed 早于
    // message.created），上方守卫靠 turnId 找回这条气泡补写正文。只在绑定这一刻写一次，
    // 不在每个事件上重复触发 setState。
    updateAssistantMessage((message) => (message.turnId === turnId ? message : { ...message, turnId }));
  } else if (activeTurnIdRef.current !== turnId) {
    return;
  }

  switch (type) {
    case 'conversation.thinking.started':
    case 'conversation.thinking.delta': {
      const delta = pickString(payload.content);
      if (!delta) return;
      updateAssistantMessage((message) => ({
        ...message,
        thinking: `${message.thinking ?? ''}${delta}`,
        thinkingLoading: true,
      }));
      return;
    }
    case 'conversation.thinking.completed': {
      const content = pickString(payload.content);
      const serverMessageId = pickString(payload.message_id) || undefined;
      updateAssistantMessage((message) => ({
        ...message,
        thinking: content || message.thinking,
        serverMessageId: message.serverMessageId ?? serverMessageId,
        streamSeqs: appendStreamSeq(message.streamSeqs, event.stream_seq),
        shareMessageIds: appendMessageId(message.shareMessageIds, serverMessageId),
        thinkingLoading: true,
      }));
      return;
    }
    case 'conversation.tool.started': {
      if (!toolCallId) return;
      const incoming: ChatMessageToolCall = {
        id: toolCallId,
        name: pickString(payload.tool_name) || 'unknown-tool',
        arguments: pickString(payload.args_preview),
      };
      updateAssistantMessage((message) => ({
        ...message,
        thinkingLoading: true,
        tool_calls: mergeToolCallsById(message.tool_calls ?? [], [incoming]),
      }));
      return;
    }
    case 'conversation.tool.completed': {
      if (!toolCallId) return;
      const output = pickString(payload.output_preview);
      const incoming: ChatMessageToolCall = {
        id: toolCallId,
        name: pickString(payload.tool_name) || 'unknown-tool',
        arguments: '',
        output: output || undefined,
        status: mapToolStatus(payload.status),
      };
      updateAssistantMessage((message) => ({
        ...message,
        thinkingLoading: true,
        tool_calls: mergeToolCallsById(message.tool_calls ?? [], [incoming]),
      }));
      return;
    }
    case 'conversation.message.delta': {
      const delta = pickString(payload.content) || pickString(payload.text_delta);
      if (!delta) return;
      updateAssistantMessage((message) => ({
        ...message,
        content: `${message.content}${delta}`,
        thinkingLoading: false,
      }));
      return;
    }
    case 'conversation.message.created': {
      // 后端有时不下发 message.delta，而是把整段正文一次性放在 message.created 里。
      // - content 若已被 delta 累出来则不覆盖（避免后端截断/审核版本盖掉更完整的累积结果）；
      // - 不调用 markStreamCompleted，留给 turn.completed 收尾。否则后续 turn.completed 会走
      //   `drop:not-streaming-and-not-resumable` 被丢弃，"思考中"状态收不掉。
      const content = pickString(payload.content);
      if (!content) return;
      const serverMessageId = pickString(payload.message_id) || undefined;
      const createdAt = new Date(event.timestamp ?? Date.now()).toISOString();
      updateAssistantMessage((message) =>
        mergeMessageCreated(message, { content, serverMessageId, streamSeq: event.stream_seq, createdAt }),
      );
      return;
    }
    case 'conversation.artifact.created': {
      const mediaPath = pickString(payload.object_key) || pickString(payload.media_path);
      if (!mediaPath) return;
      const serverMessageId = pickString(payload.message_id) || undefined;
      const normalizedType = normalizeClawMediaType(pickString(payload.media_type));

      updateAssistantMessage((message) => ({
        ...message,
        serverMessageId: serverMessageId ?? message.serverMessageId,
        streamSeqs: appendStreamSeq(message.streamSeqs, event.stream_seq),
        shareMessageIds: appendMessageId(message.shareMessageIds, serverMessageId),
        fileList: mergeAttachmentContentsByTypeAndContent(message.fileList ?? [], [
          { type: normalizedType, content: mediaPath },
        ]),
      }));
      return;
    }
    case 'conversation.turn.completed': {
      updateAssistantMessage((message) => ({
        ...message,
        thinkingLoading: false,
        createdAt: message.createdAt ?? new Date().toISOString(),
      }));
      markStreamCompleted();
      return;
    }
    case 'conversation.turn.failed': {
      const errorMessage = pickString(payload.error_message) || pickString(payload.reason);
      setAssistantErrorMessage(errorMessage.trim() || failedFallbackMessage);
      markStreamCompleted();
      return;
    }
    default:
      return;
  }
}
